/** O tour de dois minutos: que passos há e quais fazem sentido agora.
 *
 *  Um tour que aponta para um botão que não está no ecrã é pior do que
 *  não haver tour: a seta fica no vazio e quem está a aprender conclui
 *  que a aplicação está partida. Por isso cada passo declara o
 *  elemento a que se agarra, e os passos sem elemento presente são
 *  saltados em vez de mostrados.
 *
 *  Os alvos são marcados no JSX com `data-tour="..."`. É um atributo e
 *  não uma classe de propósito: uma classe do Tailwind pode ser
 *  mudada por motivos de estilo e levar o tour atrás sem ninguém dar
 *  por isso.
 */

export interface PassoTour {
  /** O valor de `data-tour` do elemento a apontar. */
  alvo: string
  titulo: string
  corpo: string
  /** De que lado do elemento fica o balão. */
  lado: 'baixo' | 'cima'
  /** Passos que não se saltam mesmo sem alvo: falam da aplicação e não
   *  de um botão. O primeiro e o último são assim. */
  semAlvo?: boolean
}

export const PASSOS: PassoTour[] = [
  {
    alvo: '',
    semAlvo: true,
    titulo: 'Dois minutos, e ficas a saber o essencial',
    corpo:
      'Esta é uma plataforma openBIM que corre inteira no teu browser. ' +
      'Os modelos não são enviados para lado nenhum: ficam na tua ' +
      'máquina. Podes sair deste guia a qualquer momento.',
    lado: 'baixo',
  },
  {
    alvo: 'abrir-modelo',
    titulo: 'Começa por abrir um modelo',
    corpo:
      'Abre um IFC do teu disco, ou arrasta-o para a janela. Também ' +
      'abrimos IFCZIP, Fragments e nuvens de pontos em LAS, LAZ, E57, ' +
      'PLY, XYZ e PTS.',
    lado: 'baixo',
  },
  {
    alvo: 'friso',
    titulo: 'O friso está agrupado pelo que queres fazer',
    corpo:
      'Ficheiro trata do que entra e sai. Vista trata da câmara e das ' +
      'vistas 2D. Aparência trata de como o modelo é desenhado. ' +
      'Medições e Anotações são o que o nome diz. Ctrl+F1 recolhe-o.',
    lado: 'baixo',
  },
  {
    alvo: 'regua',
    titulo: 'À esquerda está tudo o que podes abrir',
    corpo:
      'A régua junta num sítio só os painéis e as ferramentas: ' +
      'estrutura, propriedades, filtro, mapa, IDS, BCF, conflitos. ' +
      'O último ícone deixa-te mudar a ordem e esconder o que não usas.',
    lado: 'baixo',
  },
  {
    alvo: 'pesquisa-global',
    titulo: 'Para chegar a um elemento, procura',
    corpo:
      'Carrega em "/" e escreve um GlobalId, um nome ou uma classe IFC. ' +
      'É o caminho mais curto quando recebes um GlobalId num relatório ' +
      'de IDS ou num tópico BCF.',
    lado: 'baixo',
  },
  {
    alvo: 'barra-estado',
    titulo: 'Em baixo está sempre o estado',
    corpo:
      'Modelos e entidades carregadas, quantos elementos tens ' +
      'seleccionados, e o custo de desenhar: triângulos, imagens por ' +
      'segundo e memória. É aqui que se percebe se o modelo é pesado.',
    lado: 'cima',
  },
  {
    alvo: '',
    semAlvo: true,
    titulo: 'O resto descobre-se a usar',
    corpo:
      'Há IDS, BCF, deteção de conflitos, vistas 2D, medições, ' +
      'georreferenciação e um assistente. O menu de ajuda tem a lista ' +
      'completa e as limitações conhecidas, escritas sem enfeite.',
    lado: 'baixo',
  },
]

export const TOUR_VISTO_KEY = 'vn2r.tour.visto'

interface Armazenamento {
  getItem: (chave: string) => string | null
  setItem: (chave: string, valor: string) => void
}

function armazenamento(): Armazenamento | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function tourJaVisto(
  store: Armazenamento | null = armazenamento(),
): boolean {
  try {
    return store?.getItem(TOUR_VISTO_KEY) === '1'
  } catch {
    return false
  }
}

export function marcarTourVisto(
  store: Armazenamento | null = armazenamento(),
): void {
  try {
    store?.setItem(TOUR_VISTO_KEY, '1')
  } catch {
    /* sem onde guardar: o convite volta a aparecer, e mais nada */
  }
}

/** Os passos que se podem mesmo mostrar agora.
 *
 *  `existe` diz se um alvo está no ecrã. Injectada para os testes não
 *  precisarem de DOM. */
export function passosDisponiveis(
  existe: (alvo: string) => boolean,
  passos: readonly PassoTour[] = PASSOS,
): PassoTour[] {
  return passos.filter((p) => p.semAlvo || existe(p.alvo))
}

/** O índice do passo seguinte, ou null quando o tour acabou. */
export function proximoIndice(
  atual: number,
  total: number,
): number | null {
  const seguinte = atual + 1
  return seguinte < total ? seguinte : null
}
